import React, { useState, useEffect, useRef } from 'react';
import { Table, Button, Alert, Spinner, Form, Card, Modal, Breadcrumb } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { BlobItem } from '../models/BlobItem';
import FileViewer from './FileViewer';

const BlobList: React.FC<{
  containerName: string | null;
  initialPath?: string;
  onPathChange?: (path: string) => void;
}> = ({ containerName, initialPath = '', onPathChange }) => {
  const { storageService } = useAuth();
  const [blobs, setBlobs] = useState<BlobItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const [currentFolderPath, setCurrentFolderPath] = useState<string>(initialPath);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement | null>(null);
  const [showFileViewer, setShowFileViewer] = useState<boolean>(false);
  const [selectedFile, setSelectedFile] = useState<BlobItem | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showCreateFolderModal, setShowCreateFolderModal] = useState<boolean>(false);
  const [newFolderName, setNewFolderName] = useState<string>('');
  const [creatingFolder, setCreatingFolder] = useState<boolean>(false);
  const [selectedBlobs, setSelectedBlobs] = useState<Set<string>>(new Set());
  const [downloading, setDownloading] = useState<boolean>(false);
  const [sortField, setSortField] = useState<'name' | 'size' | 'type' | 'modified'>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // When the container changes: clear UI state and align the active folder with the URL.
  useEffect(() => {
    setSearchTerm('');
    setSelectedBlobs(new Set());
    if (containerName) {
      setCurrentFolderPath(initialPath); // Use initialPath from URL on container change
    } else {
      setBlobs([]);
      setCurrentFolderPath('');
    }
  }, [containerName, storageService]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync the active folder when the URL changes externally (e.g. browser back/forward).
  // Deliberately does NOT call onPathChange — doing so would push the URL again and corrupt history.
  useEffect(() => {
    setCurrentFolderPath(prev => (prev !== initialPath ? initialPath : prev));
  }, [initialPath]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch contents and clear selections whenever the active folder (or container) changes.
  useEffect(() => {
    setSelectedBlobs(new Set());
    if (containerName) {
      fetchBlobs(currentFolderPath);
    }
  }, [containerName, currentFolderPath, storageService]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchBlobs = async (folderPath: string = ''): Promise<void> => {
    if (!storageService || !containerName) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const blobList = await storageService.getBlobsInContainer(containerName, folderPath);
      // Filter out directory marker blobs (those ending with .dir or empty blobs that are folders)
      const filteredList = blobList.filter(blob => {
        // Skip directory markers and empty folder blobs
        if (blob.isFolder) return true;
        if (blob.name.endsWith('.dir')) return false;
        if (blob.contentLength === 0 && blob.contentType === 'application/x-directory') return false;
        return true;
      });
      setBlobs(filteredList);
    } catch (err) {
      setError('Failed to fetch blobs. Please try again.');
      console.error('Error fetching blobs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    if (!storageService || !containerName || !event.target.files || event.target.files.length === 0) return;

    const files = Array.from(event.target.files);
    setUploading(true);
    setError(null);

    try {
      // Preserve folder structure for folder uploads: put each file under the
      // relative directory from its webkitRelativePath.
      await Promise.all(files.map((file) => {
        const rel = (file as any).webkitRelativePath || '';
        const dir = rel.includes('/') ? rel.substring(0, rel.lastIndexOf('/')) : '';
        const effectiveFolder = dir
          ? (currentFolderPath ? `${currentFolderPath}/${dir}` : dir)
          : currentFolderPath;
        return storageService!.uploadBlob(containerName, file, effectiveFolder);
      }));
      await fetchBlobs(currentFolderPath);
    } catch (err) {
      setError('Failed to upload one or more files. Please try again.');
      console.error('Error uploading files:', err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      if (folderInputRef.current) {
        folderInputRef.current.value = '';
      }
    }
  };

  const handleDeleteSelected = async (): Promise<void> => {
    if (!storageService || !containerName || selectedBlobs.size === 0) return;
    const count = selectedBlobs.size;
    if (!window.confirm(`Delete ${count} selected file(s)? This action cannot be undone.`)) return;

    const selectedFiles = blobs.filter(b => !b.isFolder && selectedBlobs.has(b.path || b.name));
    setDownloading(true);
    setError(null);
    try {
      let failed = 0;
      for (const file of selectedFiles) {
        try {
          await storageService.deleteBlob(containerName, file.path || file.name, '');
        } catch (e) {
          failed++;
          console.error(`Error deleting ${file.name}:`, e);
        }
      }
      setSelectedBlobs(new Set());
      await fetchBlobs(currentFolderPath);
      if (failed > 0) {
        setError(`${failed} of ${count} file(s) could not be deleted.`);
      }
    } catch (err) {
      setError('Failed to delete selected files. Please try again.');
      console.error('Bulk delete error:', err);
    } finally {
      setDownloading(false);
    }
  };

  const handleDeleteBlob = async (blobName: string, isFolder: boolean = false): Promise<void> => {
    if (!storageService || !containerName) return;
    
    const itemType = isFolder ? 'folder' : 'file';
    if (window.confirm(`Are you sure you want to delete the ${itemType} "${blobName}"? This action cannot be undone.`)) {
      try {
        if (isFolder) {
          const folderPath = currentFolderPath
            ? (currentFolderPath.endsWith('/') ? currentFolderPath + blobName : currentFolderPath + '/' + blobName)
            : blobName;
          await storageService.deleteFolder(containerName, folderPath);
        } else {
          await storageService.deleteBlob(containerName, blobName, currentFolderPath);
        }
        await fetchBlobs(currentFolderPath);
      } catch (err) {
        setError(`Failed to delete ${itemType}. Please try again.`);
        console.error(`Error deleting ${itemType}:`, err);
      }
    }
  };

  const handleDownloadBlob = async (blobName: string): Promise<void> => {
    if (!storageService || !containerName) return;
    
    try {
      const { data, contentType } = await storageService.downloadBlob(containerName, blobName, currentFolderPath);
      
      // Create a blob URL for the downloaded data
      const blobUrl = URL.createObjectURL(data);
      
      // Create and trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = blobName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up the blob URL
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      setError('Failed to download blob. Please try again.');
      console.error('Error downloading blob:', err);
    }
  };

  const handleToggleSelect = (blobPath: string): void => {
    setSelectedBlobs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(blobPath)) {
        newSet.delete(blobPath);
      } else {
        newSet.add(blobPath);
      }
      return newSet;
    });
  };

  const handleSelectAll = (): void => {
    const allFilePaths = filteredFiles.map(file => file.path || file.name);
    if (selectedBlobs.size === allFilePaths.length && allFilePaths.every(path => selectedBlobs.has(path))) {
      // Deselect all
      setSelectedBlobs(new Set());
    } else {
      // Select all files (not folders)
      setSelectedBlobs(new Set(allFilePaths));
    }
  };

  const handleDownloadSelected = async (): Promise<void> => {
    if (!storageService || !containerName || selectedBlobs.size === 0) return;
    
    setDownloading(true);
    setError(null);
    
    try {
      const selectedFiles = filteredFiles.filter(file => {
        const path = file.path || file.name;
        return selectedBlobs.has(path);
      });

      // Download files sequentially to avoid overwhelming the browser
      for (const file of selectedFiles) {
        try {
          const { data } = await storageService.downloadBlob(containerName, file.name, currentFolderPath);
          const blobUrl = URL.createObjectURL(data);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = file.name;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(blobUrl);
          
          // Small delay between downloads to prevent browser blocking
          await new Promise(resolve => setTimeout(resolve, 100));
        } catch (err) {
          console.error(`Error downloading ${file.name}:`, err);
          // Continue with other files even if one fails
        }
      }
      
      setSelectedBlobs(new Set());
    } catch (err) {
      setError('Failed to download one or more files. Please try again.');
      console.error('Error downloading files:', err);
    } finally {
      setDownloading(false);
    }
  };

  // Single entry point for user-initiated folder navigation: update local state AND the URL.
  const navigateToFolder = (path: string): void => {
    setCurrentFolderPath(path);
    onPathChange?.(path);
  };

  // Open a file full-screen in a new browser tab (native viewing for PDFs,
  // images, HTML, etc.). Opens the tab synchronously to avoid popup blockers,
  // downloads the bytes via the AAD credential, then shows them as an object URL.
  const handleOpenInNewTab = async (file: BlobItem): Promise<void> => {
    if (!storageService || !containerName) return;
    const tab = window.open('', '_blank');
    try {
      const { data, contentType } = await storageService.downloadBlob(containerName, file.name, currentFolderPath);
      const ext = (file.name.split('.').pop() || '').toLowerCase();
      const type = (ext === 'pdf' || contentType === 'application/pdf') ? 'application/pdf'
        : (['html', 'htm'].includes(ext) || contentType.includes('html')) ? 'text/html'
        : contentType;
      const url = URL.createObjectURL(new Blob([data], { type }));
      if (tab) tab.location.href = url; else window.open(url, '_blank');
    } catch (e) {
      if (tab) tab.close();
      console.error('Open in new tab failed:', e);
      setError('Could not open the file in a new tab. Please try again.');
    }
  };

  const handleViewFile = (blob: BlobItem): void => {
    if (blob.isFolder) {
      // Navigate into folder
      const newPath = currentFolderPath
        ? (currentFolderPath.endsWith('/') ? currentFolderPath + blob.name : currentFolderPath + '/' + blob.name)
        : blob.name;
      navigateToFolder(newPath);
    } else {
      setSelectedFile(blob);
      setShowFileViewer(true);
    }
  };

  const handleCloseViewer = (): void => {
    setShowFileViewer(false);
    setSelectedFile(null);
  };

  const handleCreateFolder = async (): Promise<void> => {
    if (!storageService || !containerName || !newFolderName.trim()) return;
    
    setCreatingFolder(true);
    setError(null);
    
    try {
      const fullFolderPath = currentFolderPath 
        ? (currentFolderPath.endsWith('/') ? currentFolderPath + newFolderName.trim() : currentFolderPath + '/' + newFolderName.trim())
        : newFolderName.trim();
      
      await storageService.createFolder(containerName, fullFolderPath);
      setShowCreateFolderModal(false);
      setNewFolderName('');
      await fetchBlobs(currentFolderPath);
    } catch (err) {
      setError('Failed to create folder. Please try again.');
      console.error('Error creating folder:', err);
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleBreadcrumbClick = (path: string): void => {
    navigateToFolder(path);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Separate folders and files
  const folders = blobs.filter(blob => blob.isFolder);
  const files = blobs.filter(blob => !blob.isFolder);

  // Filter blobs based on search term
  const filteredFolders = folders.filter(folder =>
    folder.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredFiles = files.filter(file =>
    file.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    file.contentType.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedFiles = [...filteredFiles].sort((a, b) => {
    let cmp = 0;
    if (sortField === 'name') cmp = a.name.localeCompare(b.name);
    else if (sortField === 'size') cmp = (a.contentLength || 0) - (b.contentLength || 0);
    else if (sortField === 'type') cmp = (a.contentType || '').localeCompare(b.contentType || '');
    else cmp = new Date(a.lastModified).getTime() - new Date(b.lastModified).getTime();
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const toggleSort = (field: 'name' | 'size' | 'type' | 'modified'): void => {
    if (sortField === field) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir(field === 'modified' || field === 'size' ? 'desc' : 'asc'); }
  };

  const sortIcon = (field: 'name' | 'size' | 'type' | 'modified'): string => {
    if (sortField !== field) return 'bi-arrow-down-up text-muted';
    return sortDir === 'asc' ? 'bi-sort-down-alt' : 'bi-sort-up-alt';
  };

  const formatDateTime = (d: Date): string => {
    const dt = new Date(d);
    return dt.toLocaleDateString() + ' ' + dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchTerm(event.target.value);
  };

  const clearSearch = (): void => {
    setSearchTerm('');
  };

  // Build breadcrumb path
  const breadcrumbPaths = currentFolderPath ? currentFolderPath.split('/').filter(p => p) : [];
  const getBreadcrumbPath = (index: number): string => {
    return breadcrumbPaths.slice(0, index + 1).join('/');
  };

  if (!containerName) {
    return (
      <Card className="text-center py-5">
        <Card.Body>
          <i className="bi bi-arrow-left" style={{ fontSize: '3rem', color: '#ccc' }}></i>
          <h5 className="mt-3">Select a Container</h5>
          <p className="text-muted">Choose a container from the list to view files</p>
        </Card.Body>
      </Card>
    );
  }

  if (loading && blobs.length === 0) {
    return (
      <Card>
        <Card.Body className="text-center py-5">
          <Spinner animation="border" size="sm" />
          <p className="text-muted mt-2 mb-0">Loading files...</p>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Header className="bg-white border-0 d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center">
          <i className="bi bi-folder me-2 text-primary"></i>
          <h5 className="mb-0">{containerName}</h5>
        </div>
        <div className="d-flex gap-2 align-items-center">
          {selectedBlobs.size > 0 && (
            <Button
              variant="success"
              size="sm"
              onClick={handleDownloadSelected}
              disabled={downloading}
            >
              <i className="bi bi-download me-2"></i>
              {downloading ? `Downloading ${selectedBlobs.size}...` : `Download Selected (${selectedBlobs.size})`}
            </Button>
          )}
          {selectedBlobs.size > 0 && (
            <Button
              variant="outline-danger"
              size="sm"
              onClick={handleDeleteSelected}
              disabled={downloading}
            >
              <i className="bi bi-trash3 me-2"></i>
              Delete Selected ({selectedBlobs.size})
            </Button>
          )}
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setShowCreateFolderModal(true)}
          >
            <i className="bi bi-folder-plus me-2"></i>
            New Folder
          </Button>
          <Form.Control
            type="file"
            multiple
            ref={fileInputRef}
            onChange={handleFileUpload}
            style={{ display: 'none' }}
            id="fileUpload"
          />
          <input
            type="file"
            multiple
            onChange={handleFileUpload}
            style={{ display: 'none' }}
            ref={(node) => {
              folderInputRef.current = node;
              if (node) { node.setAttribute('webkitdirectory', ''); node.setAttribute('directory', ''); }
            }}
          />
          <Button
            variant="primary"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
          >
            <i className="bi bi-cloud-upload me-2"></i>
            {uploading ? 'Uploading...' : 'Upload Files'}
          </Button>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => folderInputRef.current?.click()}
            disabled={uploading}
            title="Upload an entire folder (keeps its structure)"
          >
            <i className="bi bi-folder-symlink me-2"></i>
            Upload Folder
          </Button>
        </div>
      </Card.Header>
      
      {/* Breadcrumb Navigation */}
      {(currentFolderPath || breadcrumbPaths.length > 0) && (
        <div className="px-3 pt-3 pb-2">
          <Breadcrumb>
            <Breadcrumb.Item
              onClick={() => handleBreadcrumbClick('')}
              style={{ cursor: 'pointer' }}
              active={breadcrumbPaths.length === 0}
            >
              <i className="bi bi-house-door me-1"></i>
              {containerName}
            </Breadcrumb.Item>
            {breadcrumbPaths.map((pathSegment, index) => (
              <Breadcrumb.Item
                key={index}
                onClick={() => handleBreadcrumbClick(getBreadcrumbPath(index))}
                style={{ cursor: 'pointer' }}
                active={index === breadcrumbPaths.length - 1}
              >
                {pathSegment}
              </Breadcrumb.Item>
            ))}
          </Breadcrumb>
        </div>
      )}
      
      {/* Search Bar */}
      {blobs.length > 0 && (
        <div className="px-3 pt-2 pb-2">
          <Form.Group className="mb-0">
            <div className="position-relative">
              <i className="bi bi-search position-absolute" style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d', zIndex: 10 }}></i>
              <Form.Control
                type="text"
                placeholder="Search files by name or type..."
                value={searchTerm}
                onChange={handleSearchChange}
                style={{ paddingLeft: '38px', paddingRight: searchTerm ? '38px' : '12px' }}
              />
              {searchTerm && (
                <Button
                  variant="link"
                  size="sm"
                  className="position-absolute"
                  style={{ right: '4px', top: '50%', transform: 'translateY(-50%)', padding: '4px 8px', color: '#6c757d' }}
                  onClick={clearSearch}
                  title="Clear search"
                >
                  <i className="bi bi-x-circle-fill"></i>
                </Button>
              )}
            </div>
            {searchTerm && (
              <small className="text-muted mt-1 d-block">
                Found {filteredFolders.length + filteredFiles.length} of {blobs.length} item{blobs.length !== 1 ? 's' : ''}
              </small>
            )}
          </Form.Group>
        </div>
      )}

      <Card.Body className="p-0">
        {error && <Alert variant="danger" className="m-3">{error}</Alert>}

        {blobs.length === 0 ? (
          <div className="text-center py-5">
            <i className="bi bi-inbox" style={{ fontSize: '3.5rem', color: '#ccc' }}></i>
            <p className="text-muted mt-3 mb-3">No files or folders in this location</p>
            <div className="d-flex justify-content-center gap-2">
              <Button 
                variant="outline-primary" 
                size="sm"
                onClick={() => setShowCreateFolderModal(true)}
              >
                <i className="bi bi-folder-plus me-2"></i>
                Create Folder
              </Button>
              <Button 
                variant="outline-primary" 
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                <i className="bi bi-cloud-upload me-2"></i>
                Upload Files
              </Button>
            </div>
          </div>
        ) : (filteredFolders.length === 0 && filteredFiles.length === 0) ? (
          <div className="text-center py-5">
            <i className="bi bi-search" style={{ fontSize: '3.5rem', color: '#ccc' }}></i>
            <p className="text-muted mt-3 mb-3">No items match your search "{searchTerm}"</p>
            <Button 
              variant="outline-secondary" 
              size="sm"
              onClick={clearSearch}
            >
              <i className="bi bi-x-circle me-2"></i>
              Clear Search
            </Button>
          </div>
        ) : (
          <Table responsive hover className="mb-0">
            <thead>
              <tr>
                <th style={{ width: '30px' }}>
                  <Form.Check
                    type="checkbox"
                    checked={filteredFiles.length > 0 && filteredFiles.every(file => {
                      const path = file.path || file.name;
                      return selectedBlobs.has(path);
                    })}
                    onChange={handleSelectAll}
                    title="Select all files"
                  />
                </th>
                <th style={{ width: '35%', maxWidth: '400px', cursor: 'pointer' }} onClick={() => toggleSort('name')}>
                  Name <i className={`bi ${sortIcon('name')} ms-1 small`}></i>
                </th>
                <th style={{ width: '10%', minWidth: '80px', cursor: 'pointer' }} onClick={() => toggleSort('size')}>
                  Size <i className={`bi ${sortIcon('size')} ms-1 small`}></i>
                </th>
                <th style={{ width: '12%', minWidth: '100px', cursor: 'pointer' }} onClick={() => toggleSort('type')}>
                  Type <i className={`bi ${sortIcon('type')} ms-1 small`}></i>
                </th>
                <th style={{ width: '18%', minWidth: '150px', cursor: 'pointer' }} onClick={() => toggleSort('modified')}>
                  Last Modified <i className={`bi ${sortIcon('modified')} ms-1 small`}></i>
                </th>
                <th className="text-center" style={{ width: '20%', minWidth: '120px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {/* Folders first */}
              {filteredFolders.map((folder) => (
                <tr key={folder.path || folder.name} style={{ cursor: 'pointer' }} onClick={() => handleViewFile(folder)}>
                  <td onClick={(e) => e.stopPropagation()}>
                    {/* No checkbox for folders */}
                  </td>
                  <td style={{ maxWidth: '400px' }}>
                    <div className="d-flex align-items-center">
                      <i className="bi bi-folder-fill me-2 text-warning" style={{ flexShrink: 0 }}></i>
                      <span className="file-name-truncate" title={folder.name}>
                        <strong>{folder.name}</strong>
                      </span>
                    </div>
                  </td>
                  <td>-</td>
                  <td>
                    <span className="badge bg-warning text-dark" style={{ fontSize: '0.75rem' }}>
                      FOLDER
                    </span>
                  </td>
                  <td>
                    {'—'}
                  </td>
                  <td>
                    <div className="d-flex justify-content-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <Button 
                        variant="light"
                        size="sm"
                        className="text-danger"
                        onClick={() => handleDeleteBlob(folder.name, true)}
                        title="Delete folder"
                      >
                        <i className="bi bi-trash3"></i>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {/* Files */}
              {sortedFiles.map((file) => {
                const filePath = file.path || file.name;
                const isSelected = selectedBlobs.has(filePath);
                return (
                  <tr key={filePath}>
                    <td onClick={(e) => e.stopPropagation()}>
                      <Form.Check
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => handleToggleSelect(filePath)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td style={{ maxWidth: '400px' }}>
                      <div className="d-flex align-items-center">
                        <i className="bi bi-file-earmark-text me-2 text-muted" style={{ flexShrink: 0 }}></i>
                        <span className="file-name-truncate" title={file.name}>
                          {file.name}
                        </span>
                      </div>
                    </td>
                  <td>{formatFileSize(file.contentLength)}</td>
                  <td>
                    <span className="badge bg-light text-dark" style={{ fontSize: '0.75rem' }} title={file.contentType}>
                      {file.contentType.split('/')[1]?.toUpperCase().substring(0, 8) || 'FILE'}
                    </span>
                  </td>
                  <td title={file.lastModified.toLocaleString()} style={{ whiteSpace: 'nowrap' }}>
                    {formatDateTime(file.lastModified)}
                  </td>
                  <td>
                    <div className="d-flex justify-content-center gap-1">
                      <Button
                        variant="light"
                        size="sm"
                        onClick={() => handleViewFile(file)}
                        title="Preview file"
                      >
                        <i className="bi bi-eye"></i>
                      </Button>
                      <Button
                        variant="light"
                        size="sm"
                        onClick={() => handleOpenInNewTab(file)}
                        title="Open in new tab"
                      >
                        <i className="bi bi-box-arrow-up-right"></i>
                      </Button>
                      <Button
                        variant="light"
                        size="sm"
                        onClick={() => handleDownloadBlob(file.name)}
                        title="Download file"
                      >
                        <i className="bi bi-download"></i>
                      </Button>
                      <Button 
                        variant="light"
                        size="sm"
                        className="text-danger"
                        onClick={() => handleDeleteBlob(file.name, false)}
                        title="Delete file"
                      >
                        <i className="bi bi-trash3"></i>
                      </Button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </Table>
        )}
      </Card.Body>

      {/* Create Folder Modal */}
      <Modal show={showCreateFolderModal} onHide={() => {
        setShowCreateFolderModal(false);
        setNewFolderName('');
      }}>
        <Modal.Header closeButton>
          <Modal.Title>Create New Folder</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Folder Name</Form.Label>
            <Form.Control 
              type="text" 
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Enter folder name"
              onKeyPress={(e) => {
                if (e.key === 'Enter' && newFolderName.trim()) {
                  handleCreateFolder();
                }
              }}
            />
            <Form.Text className="text-muted">
              {currentFolderPath && (
                <>Folder will be created in: <strong>{currentFolderPath}</strong></>
              )}
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowCreateFolderModal(false);
            setNewFolderName('');
          }}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleCreateFolder}
            disabled={!newFolderName.trim() || creatingFolder}
          >
            {creatingFolder ? 'Creating...' : 'Create'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* File Viewer Modal */}
      {selectedFile && !selectedFile.isFolder && (
        <FileViewer
          show={showFileViewer}
          onHide={handleCloseViewer}
          containerName={containerName}
          fileName={selectedFile.path || selectedFile.name}
          contentType={selectedFile.contentType}
          fileSize={selectedFile.contentLength}
        />
      )}
    </Card>
  );
};

export default BlobList;
